import {
  GamoraBase64URLGuard,
  GamoraErrorUnhandledException,
  assert,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakServiceSessionGuard,
  HoruzProtocolChulakServiceSessionGuardInput,
} from '../service-session.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageGuard } from './base.js';

/**
 * Input type
 * @public
 */
export type HoruzProtocolChulakMessageAuthCompletedGuardInput = {
  serviceSession: HoruzProtocolChulakServiceSessionGuardInput;
};

/**
 * Auth
 * @public
 */
export class HoruzProtocolChulakMessageAuthCompletedGuard extends HoruzProtocolChulakMessageGuard<
  HoruzProtocolChulakDataType.AUTH_COMPLETED,
  HoruzProtocolChulakMessageAuthCompletedGuardInput
> {
  private _serviceSession: HoruzProtocolChulakServiceSessionGuard =
    HoruzProtocolChulakServiceSessionGuard.Empty();

  /**
   * Constructor
   * @param input - the input
   */
  constructor(input: HoruzProtocolChulakMessageAuthCompletedGuardInput) {
    super();
    this._serviceSession = HoruzProtocolChulakServiceSessionGuard.fromJSON(
      input.serviceSession,
    );
  }

  /**
   * Gets type
   */
  get type(): HoruzProtocolChulakDataType.AUTH_COMPLETED {
    return HoruzProtocolChulakDataType.AUTH_COMPLETED;
  }

  /**
   * AuthenticationCompact
   */
  get serviceSession() {
    return this._serviceSession;
  }
  set serviceSession(serviceSession: HoruzProtocolChulakServiceSessionGuard) {
    assert(
      serviceSession instanceof HoruzProtocolChulakServiceSessionGuard,
      'Invalid serviceSession: not a HoruzProtocolChulakServiceSessionGuard.',
    );
    this._serviceSession = serviceSession;
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakMessageAuthCompletedGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakMessageAuthCompletedGuard(
      input as HoruzProtocolChulakMessageAuthCompletedGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakMessageAuthCompletedGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakMessageAuthCompletedGuard(
      json as HoruzProtocolChulakMessageAuthCompletedGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON() {
    return {
      serviceSession: this._serviceSession.toJSON(),
    };
  }

  /**
   * To Base64URL
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Create clone
   */
  clone() {
    return HoruzProtocolChulakMessageAuthCompletedGuard.fromJSON(this.toJSON());
  }
}
